import { Link } from "react-router-dom";
import { site } from "@/config/site";
import { CtaBand } from "@/components/CtaBand";
import { Seo } from "@/components/Seo";
import { SignalCard } from "@/components/SignalCard";
import { NetworkAnalysisGraph } from "@/features/network-analysis/NetworkAnalysisGraph";
import { track } from "@/lib/analytics";
import { thinking } from "@/lib/content/load";
import { formatDate } from "@/lib/dates";
import { headingText } from "@/lib/heading";
import { sourceLabel } from "@/features/editorial/filters";

const notes = thinking.slice(0, 3);

export function HomePage() {
  return (
    <>
      <Seo
        title="Jay van Zyl"
        description={site.description}
        path="/"
      />
      <section className="wrap hero">
        <div>
          <p className="eyebrow">
            {site.jobTitle}, {site.worksFor}
          </p>
          <h1>Human systems and the platforms they require</h1>
          <p className="lede">{site.proposition}</p>
          <div className="hero-actions">
            <Link
              className="button"
              to="/work-with-jay"
              onClick={() => track("pathway-selected", { path: "business" })}
            >
              Start a conversation
            </Link>
            <Link
              className="button-secondary"
              to="/work"
              onClick={() => track("pathway-selected", { path: "product" })}
            >
              Explore the systems
            </Link>
          </div>
        </div>
        <figure className="portrait">
          <img
            src={site.portraits.home.src}
            alt={site.portraits.home.alt}
            width={site.portraits.home.width}
            height={site.portraits.home.height}
          />
        </figure>
      </section>

      <section className="wrap section">
        <p className="section-kicker">The through-line</p>
        <p className="lede">
          The first half of the work was the how of innovation: product lines,
          capability models, and the systems that take an idea into a business.
          The question then moved to why people act, and how they meet the
          systems around them. The current work is two platforms that have to
          run — prediction in the interaction, and architecture that still
          exists after the diagram.
        </p>
        <p>
          <Link to="/about">The longer path</Link>
        </p>
      </section>

      <section className="wrap section">
        <p className="section-kicker">See the relationships</p>
        <h2>Prediction, then tracking the graph</h2>
        <p className="lede">
          A labeled synthetic bank graph. Radius is centrality. Click a node.
        </p>
        <NetworkAnalysisGraph compact />
        <p className="meta" id="computational-social-science">
          Computational social science · Network analysis · Platform
          architecture · Prediction in production
        </p>
        <p>
          <Link to="/work/network-analysis">Read the method</Link>
        </p>
      </section>

      <section className="wrap section">
        <p className="section-kicker">What I build</p>
        <div className="grid-2">
          <SignalCard
            eyebrow="ecosystem.Ai"
            title="Behavioral prediction in production"
            to="/work/ecosystem-ai"
          >
            <p>
              Workbench, prediction server, Client Pulse Responder. Score while
              the person is still acting.
            </p>
            <p>
              <a
                href={site.profiles.developer}
                onClick={() => track("product-doc-clicked", { dest: "docs" })}
              >
                Docs
              </a>
              {" · "}
              <Link to="/work/ecosystem-ai">Case study</Link>
            </p>
          </SignalCard>
          <SignalCard
            eyebrow="EcosystemCode"
            title="Architecture made executable"
            to="/work/ecosystemcode"
          >
            <p>
              Words → validated UML and ontology → a deterministic full-stack baseline.
              Add AI only where you turn it on.
            </p>
            <p>
              <a href={site.profiles.ecosystemCode}>Product</a>
              {" · "}
              <Link to="/work/ecosystemcode">Case study</Link>
            </p>
          </SignalCard>
        </div>
        <p>
          A personal case for both. The prediction platform is a workbench, a
          prediction server, the Client Pulse Responder, and data agents.
          EcosystemCode is the modeling
          workbench plus the{" "}
          <a href={site.profiles.ontology}>public domain catalog</a>.
        </p>
      </section>

      <section className="wrap section">
        <p className="section-kicker">Proof</p>
        <div className="grid-4">
          <article className="card">
            <h3>Books</h3>
            <p>
              <Link to="/resources/built-to-thrive">Built to Thrive</Link> and{" "}
              <Link to="/resources/process-innovation-imperative">
                Process Innovation Imperative
              </Link>
              .
            </p>
          </article>
          <article className="card">
            <h3>Patents</h3>
            <p>
              <Link to="/resources#patents">
                Business networking and innovation management
              </Link>
              .
            </p>
          </article>
          <article className="card">
            <h3>Public engineering</h3>
            <p>
              <Link to="/work/open-source">ecogenetic</Link> runtime, algorithms, ontology
              catalog.
            </p>
          </article>
          <article className="card">
            <h3>Custom GPTs</h3>
            <p>
              <a
                href="https://chatgpt.com/g/g-ZS1LEEzIv-built-to-thrive-in-the-world-of-ai"
                onClick={() => track("book-gpt-clicked")}
              >
                Built to Thrive GPT
              </a>{" "}
              and product helpers.
            </p>
          </article>
        </div>
      </section>

      <section className="wrap section">
        <p className="section-kicker">How I work</p>
        <h2>Workshops that end in a decision</h2>
        <p className="lede">
          Innovation systems, network analysis, platform strategy, digital strategy and
          delivery, and intelligent automation — plus the new world of work, the AI-first
          enterprise, and The Human Organism as Algorithm. No promised outcomes. No borrowed
          client logos.
        </p>
        <Link className="button" to="/work-with-jay">
          See the offers
        </Link>
      </section>

      <section className="wrap section">
        <p className="section-kicker">Field notes</p>
        <div className="grid-3">
          {notes.map((note) => (
            <article className="card" key={note.meta.slug}>
              <p className="meta">
                {sourceLabel(note.meta.type)} · {formatDate(note.meta.sitePublishedAt)}
              </p>
              <h3>
                <Link to={note.meta.canonicalPath}>{headingText(note.meta.title)}</Link>
              </h3>
              <p>{note.meta.summary}</p>
            </article>
          ))}
        </div>
      </section>

      <CtaBand />
    </>
  );
}
